import { Injectable, computed, signal } from '@angular/core';
import { Difficulty } from '../data/address-config';
import { CPU_TEAMS } from '../generated/cpu-teams';
import { MonMetadata } from '../generated/mon';

export const CPU_DIFFICULTY_KEY = 'cpu_difficulty';
const BATTLES_PLAYED_KEY = 'battles_played';
const CPU_TEAMS_UNLOCKED_KEY = 'cpu_teams_unlocked';
const CPU_CURRENT_BATTLE_TEAM_KEY = 'cpu_current_battle_team';

const MAX_TEAM_INDEX = CPU_TEAMS.length - 1;

/** Battles played before each tier opens. */
export const DIFFICULTY_UNLOCK_AT: Record<Difficulty, number> = {
  easy: 0,
  medium: 1,
  hard: 2,
  'extra-hard': 3,
};

function readDifficulty(): Difficulty {
  const v = localStorage.getItem(CPU_DIFFICULTY_KEY);
  return v === 'medium' || v === 'hard' || v === 'extra-hard' ? v : 'easy';
}

function readBattlesPlayed(): number {
  const parsed = parseInt(localStorage.getItem(BATTLES_PLAYED_KEY) ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function readUnlockedTeamCount(): number {
  const stored = localStorage.getItem(CPU_TEAMS_UNLOCKED_KEY);
  return stored ? parseInt(stored, 10) : 0;
}

function readCurrentBattleTeam(): number | null {
  const stored = localStorage.getItem(CPU_CURRENT_BATTLE_TEAM_KEY);
  return stored !== null ? parseInt(stored, 10) : null;
}

/** `battlesPlayed` is deliberately local — it doesn't follow a player across devices. */
@Injectable({ providedIn: 'root' })
export class CpuBattlePrefsService {
  private readonly _battlesPlayed = signal(readBattlesPlayed());
  private readonly _difficulty = signal<Difficulty>(readDifficulty());
  private readonly _unlockedTeamCount = signal(readUnlockedTeamCount());
  private readonly _currentBattleTeam = signal(readCurrentBattleTeam());

  readonly battlesPlayed = this._battlesPlayed.asReadonly();
  readonly difficulty = this._difficulty.asReadonly();
  readonly unlockedTeamCount = this._unlockedTeamCount.asReadonly();
  readonly currentBattleTeam = this._currentBattleTeam.asReadonly();

  readonly allTeamsUnlocked = computed(() => this._unlockedTeamCount() >= MAX_TEAM_INDEX);

  // The tier of the CPU battle in play — per-battle, unlike the persisted picker `difficulty`.
  readonly battleDifficulty = signal<Difficulty>('medium');

  /** Called on WIN *and* LOSE — the gate is battles played, not won. */
  recordBattlePlayed(): void {
    const next = this._battlesPlayed() + 1;
    localStorage.setItem(BATTLES_PLAYED_KEY, String(next));
    this._battlesPlayed.set(next);
  }

  setDifficulty(d: Difficulty): void {
    if (d === this._difficulty()) return;
    localStorage.setItem(CPU_DIFFICULTY_KEY, d);
    this._difficulty.set(d);
  }

  unlockNextTeam(defeatedTeamIndex: number): void {
    this.setUnlockedTeamCount(Math.max(this._unlockedTeamCount(), defeatedTeamIndex + 1));
  }

  unlockAllTeams(): void {
    this.setUnlockedTeamCount(MAX_TEAM_INDEX);
  }

  private setUnlockedTeamCount(next: number): void {
    if (next > MAX_TEAM_INDEX || next === this._unlockedTeamCount()) return;
    localStorage.setItem(CPU_TEAMS_UNLOCKED_KEY, String(next));
    this._unlockedTeamCount.set(next);
  }

  /** Set while launching a numbered CPU team, so a win can unlock the next one. */
  setCurrentBattleTeam(teamIndex: number): void {
    localStorage.setItem(CPU_CURRENT_BATTLE_TEAM_KEY, String(teamIndex));
    this._currentBattleTeam.set(teamIndex);
  }

  clearCurrentBattleTeam(): void {
    localStorage.removeItem(CPU_CURRENT_BATTLE_TEAM_KEY);
    this._currentBattleTeam.set(null);
  }

  readonly lockedTiers = computed<Difficulty[]>(() =>
    (Object.keys(DIFFICULTY_UNLOCK_AT) as Difficulty[]).filter(
      (d) => this._battlesPlayed() < DIFFICULTY_UNLOCK_AT[d],
    ),
  );

  // The roster the CPU pickers offer, gated by the same unlock count. Shared so the
  // desktop modal and the mobile pane can't drift on labels or unlock rules.
  readonly cpuTeams = computed(() =>
    CPU_TEAMS.map((monIndices, index) => ({
      index,
      label: `CPU ${index + 1}`,
      mons: monIndices.map((i) => MonMetadata[i as keyof typeof MonMetadata]),
      isUnlocked: index <= this._unlockedTeamCount(),
    })),
  );

  // The authored-team ladder: Extra Hard shares Hard's 3-team clear. Distinct from
  // the battles-played `lockedTiers` above, which deck home uses. Shared by the
  // desktop CPU modal and the mobile play sheet's CPU pane.
  readonly ladderLockedTiers = computed<Difficulty[]>(() =>
    this.allTeamsUnlocked() ? [] : ['hard', 'extra-hard'],
  );
}
